/**
 * The Venom Altar (PLAN §5): 12 trash packs (3–6 mobs) + 3 bosses, forces available
 * ≈ 1.25× required (+patrols: plus 3 waypoint patrols and 3 wanderers, ≈ 1.5× in all), timer 21:50. Pack compositions mirror Altar of Fangs clusters; since v2 they stand in an
 * original monastery layout (venomAltarLayout.ts, docs/design/dungeon-layout-study.md). The 8
 * archetypes map onto the real mobs:
 *   Venom Leech/Living Venom → Venomspawn · Blade/Bloodletter → Fang Reaver
 *   High Evolutionist → Cult Adept · Primal Serpent → Spitting Serpent
 *   Twinfang Harrower (CC) → Coil Mesmerist · Ritual Chieftain/Rattling Writhe → Hooded Constrictor
 *   Ascendant Serpent → Elder Basilisk · (healer) → Venom Priestess
 *
 * Default route skips va_p03 and va_p10 (10 packs + 3 bosses, ~108% forces).
 */
import type { DungeonDef, NpcId, PackDef, PackLink, PatrolPoint, SpawnMovement, SpawnPoint, Vec2 } from '@keystone/sim';
import { DUNGEON, ENCOUNTER, NPC } from '../ids';
import { cluster } from './geometry';
import { BOUNDS, CORRIDORS, DOORS, ENTRANCE, M, OBSTACLES, PROP_HINTS, ROOMS, type VenomRoomId } from './venomAltarLayout';
import { venomAltarWorldObjects } from './venomAltarWorld'; // +world-resources

const TRASH_AGGRO = 12;
const TRASH_SOCIAL = 10;
const BOSS_AGGRO = 15;

interface PackSpec {
  id: string;
  room: VenomRoomId;
  /** Centre in source map units. */
  at: [number, number];
  /** Map point the pack faces (where the group arrives from). */
  face: [number, number];
  mobs: NpcId[];
  encounterId?: string;
  /**
   * +patrols (docs/design/mob-movement.md): 'linked' packs pull as one; 'loose' packs stand in
   * clumps more than ASSIST_RADIUS apart, so an edge or line-of-sight pull takes one clump.
   */
  link?: PackLink;
  /** +patrols: loose packs: mobs per clump and each clump's centre (the mobs fill clumps in order). */
  clumps?: { n: number; at: [number, number] }[];
  /** +patrols: per-mob movement by index (wanderers inside a pack). */
  movement?: Record<number, SpawnMovement>;
}

/** +patrols: a waypoint patrol (the leader walks, the rest follow in formation; always linked). */
interface PatrolSpec {
  id: string;
  room: VenomRoomId;
  mobs: NpcId[];
  /** Waypoints (map units); the leader spawns on the first, facing the second. */
  points: { at: [number, number]; waitMs?: number }[];
  mode: 'loop' | 'pingpong';
}

/** +patrols: a lone wanderer (TC MovementType 1). */
interface WandererSpec {
  id: string;
  room: VenomRoomId;
  mob: NpcId;
  at: [number, number];
  face: [number, number];
  radius: number;
}

const { venomspawn, fangReaver, cultAdept, spittingSerpent, coilMesmer, venomPriestess, hoodedConstrictor, elderBasilisk } = NPC;

const PACKS: PackSpec[] = [
  // Shedding Gate (narthex). The venomspawn swarm shuffles about (two small wanderers) but pulls as one.
  { id: 'va_p01', room: 'gate', at: [137, 22], face: [148, 8], mobs: [venomspawn, venomspawn, venomspawn, venomspawn, venomspawn], link: 'linked', movement: { 2: { type: 'wander', radius: 2.5 }, 4: { type: 'wander', radius: 2.5 } } },
  { id: 'va_p02', room: 'gate', at: [159, 38], face: [148, 14], mobs: [fangReaver, cultAdept, fangReaver, venomspawn, venomspawn], link: 'linked', movement: { 3: { type: 'wander', radius: 2 }, 4: { type: 'wander', radius: 2 } } },
  // Serpent Cloister (open courtyard). The route packs keep 11+ yd off the arcade walk the patrol
  // loops; the optional p03 stands right by it (pull the patrol there and it comes too).
  { id: 'va_p03', room: 'cloister', at: [162, 124], face: [148, 108], mobs: [spittingSerpent, venomspawn, spittingSerpent, venomspawn, venomspawn], link: 'linked' },
  { id: 'va_p04', room: 'cloister', at: [157, 96], face: [148, 80], mobs: [hoodedConstrictor, fangReaver, spittingSerpent, fangReaver], link: 'linked' },
  { id: 'va_p05', room: 'cloister', at: [137, 118], face: [160, 96], mobs: [fangReaver, coilMesmer, fangReaver, venomPriestess], link: 'linked' },
  // Ravager's Pit
  { id: 'va_ravager', room: 'ravager_pit', at: [52, 102], face: [88, 108], mobs: [NPC.ravager], encounterId: ENCOUNTER.ravager },
  // Scale Scriptorium
  { id: 'va_p06', room: 'scriptorium', at: [36, 182], face: [58, 168], mobs: [fangReaver, cultAdept, fangReaver, fangReaver], link: 'linked' },
  {
    id: 'va_p07', room: 'scriptorium', at: [78, 190], face: [36, 182], mobs: [fangReaver, venomPriestess, fangReaver, cultAdept], link: 'loose',
    clumps: [{ n: 2, at: [71, 187] }, { n: 2, at: [87, 195] }],
  },
  // Writhing Hatchery (mine wing)
  { id: 'va_p08', room: 'hatchery', at: [148, 200], face: [126, 196], mobs: [hoodedConstrictor, cultAdept, fangReaver, cultAdept], link: 'linked' },
  // Coil Cistern
  { id: 'va_coil', room: 'coil_cistern', at: [226, 208], face: [202, 200], mobs: [NPC.theCoil], encounterId: ENCOUNTER.coil },
  // Venom Crypt
  {
    id: 'va_p09', room: 'venom_crypt', at: [214, 298], face: [240, 284], mobs: [fangReaver, coilMesmer, spittingSerpent, fangReaver, spittingSerpent], link: 'loose',
    clumps: [{ n: 3, at: [207, 297] }, { n: 2, at: [222, 289] }],
  },
  {
    id: 'va_p10', room: 'venom_crypt', at: [204, 270], face: [224, 262], mobs: [hoodedConstrictor, venomspawn, venomspawn, venomspawn, venomspawn], link: 'loose',
    clumps: [{ n: 3, at: [204, 270] }, { n: 2, at: [218, 266] }],
    movement: { 3: { type: 'wander', radius: 2 } },
  },
  // Ascendant Treasury
  { id: 'va_p11', room: 'treasury', at: [296, 284], face: [278, 284], mobs: [elderBasilisk, cultAdept, cultAdept], link: 'linked' },
  // Fang Stair
  { id: 'va_p12', room: 'fang_stair', at: [228, 352], face: [226, 334], mobs: [hoodedConstrictor, fangReaver, venomPriestess, fangReaver], link: 'linked' },
  // The Fang Altar
  { id: 'va_chieftain', room: 'fang_altar', at: [226, 421], face: [226, 394], mobs: [NPC.chieftain], encounterId: ENCOUNTER.chieftain },
];

/**
 * +patrols: roaming mobs (docs/design/mob-movement.md §6). Not on the route: the tank waits for
 * them to pass or pulls them away from the packs. Patrol points sit on the arcade walk, along the
 * library hall and up the grand stair; paths go through doorways and over the flights (the
 * renderer lifts them onto the stairs from the corridor rises).
 */
const PATROLS: PatrolSpec[] = [
  // Cloister watch: loops the covered arcade around the garth, past p04 (south walk) and p03 (east walk).
  {
    id: 'va_pat_cloister',
    room: 'cloister',
    mobs: [fangReaver, venomspawn, venomspawn],
    points: [{ at: [175, 112] }, { at: [175, 135], waitMs: 2500 }, { at: [121, 135] }, { at: [121, 81], waitMs: 2500 }, { at: [175, 81] }],
    mode: 'loop',
  },
  // Library warden pair: along the north shelves, then down the gap between p06 and p07 to the door and back.
  {
    id: 'va_pat_scriptorium',
    room: 'scriptorium',
    mobs: [cultAdept, venomspawn],
    points: [{ at: [30, 201], waitMs: 3000 }, { at: [55, 201] }, { at: [55, 174], waitMs: 3000 }],
    mode: 'pingpong',
  },
  // Stair sentries: up and down the grand stair between the crypt (by p09) and the stair hall (by p12).
  {
    id: 'va_pat_stair',
    room: 'fang_stair',
    mobs: [fangReaver, fangReaver],
    points: [{ at: [208, 340], waitMs: 3000 }, { at: [226, 333] }, { at: [226, 307], waitMs: 4000 }],
    mode: 'pingpong',
  },
];

const WANDERERS: WandererSpec[] = [
  // The mine wing's hatchlings creep around the egg clutches either side of p08.
  { id: 'va_w01', room: 'hatchery', mob: venomspawn, at: [134, 186], face: [148, 200], radius: 6 },
  { id: 'va_w02', room: 'hatchery', mob: venomspawn, at: [163, 216], face: [148, 200], radius: 5 },
  // A serpent prowls the crypt's east pools by the treasury passage.
  { id: 'va_w03', room: 'venom_crypt', mob: spittingSerpent, at: [244, 268], face: [226, 284], radius: 6 },
];

const r1 = (v: number): number => Math.round(v * 10) / 10;

/** Loose packs: each clump is a small cluster (first mob in the middle, the rest on a 2 yd ring). */
function clumpSpawns(p: PackSpec, face: Vec2): SpawnPoint[] {
  const out: SpawnPoint[] = [];
  let i = 0;
  for (const c of p.clumps!) {
    out.push(...cluster(M.pt(c.at[0], c.at[1]), p.mobs.slice(i, i + c.n), face, 2));
    i += c.n;
  }
  return out;
}

function toPackDef(p: PackSpec): PackDef {
  const center = M.pt(p.at[0], p.at[1]);
  const ring = p.mobs.length > 4 ? 4 : 3.5;
  const face = M.pt(p.face[0], p.face[1]);
  const spawns = p.clumps ? clumpSpawns(p, face) : cluster(center, p.mobs, face, ring);
  for (const [i, m] of Object.entries(p.movement ?? {})) spawns[Number(i)]!.movement = m;
  const def: PackDef = {
    id: p.id,
    roomId: p.room,
    spawns,
    aggroRadius: p.encounterId ? BOSS_AGGRO : TRASH_AGGRO,
    socialRadius: TRASH_SOCIAL,
  };
  if (p.link) def.link = p.link;
  if (p.encounterId) def.encounterId = p.encounterId;
  return def;
}

/** Formation: the leader on the first point facing the second; the rest two abreast behind it. */
function patrolDef(p: PatrolSpec): PackDef {
  const pts: PatrolPoint[] = p.points.map((q) => ({ pos: M.pt(q.at[0], q.at[1]), ...(q.waitMs ? { waitMs: q.waitMs } : {}) }));
  const lead = pts[0]!.pos;
  const next = pts[1]!.pos;
  const facing = Math.round(Math.atan2(next.y - lead.y, next.x - lead.x) * 1000) / 1000;
  const fx = Math.cos(facing);
  const fy = Math.sin(facing);
  const spawns: SpawnPoint[] = p.mobs.map((npcId, i) => {
    if (i === 0) return { npcId, pos: lead, facing };
    const row = Math.ceil(i / 2);
    const lateral = p.mobs.length === 2 ? 0 : i % 2 ? 1.6 : -1.6;
    const along = -2.8 * row;
    return { npcId, pos: { x: r1(lead.x + fx * along - fy * lateral), y: r1(lead.y + fy * along + fx * lateral) }, facing };
  });
  // Walk the remaining points first, then back to where it started.
  return {
    id: p.id,
    roomId: p.room,
    spawns,
    aggroRadius: TRASH_AGGRO,
    socialRadius: TRASH_SOCIAL,
    link: 'linked',
    path: { points: p.mode === 'loop' ? [...pts.slice(1), pts[0]!] : pts, mode: p.mode },
  };
}

function wandererDef(w: WandererSpec): PackDef {
  const pos = M.pt(w.at[0], w.at[1]);
  const facing = Math.round(Math.atan2(w.face[1] - w.at[1], w.face[0] - w.at[0]) * 1000) / 1000;
  return {
    id: w.id,
    roomId: w.room,
    spawns: [{ npcId: w.mob, pos, facing, movement: { type: 'wander', radius: w.radius } }],
    aggroRadius: TRASH_AGGRO,
    socialRadius: TRASH_SOCIAL,
    link: 'loose',
  };
}

/** Ordered pull list the tank bot follows (MDT-style). */
export const VENOM_ALTAR_ROUTE = [
  'va_p01',
  'va_p02',
  'va_p04',
  'va_p05',
  'va_ravager',
  'va_p06',
  'va_p07',
  'va_p08',
  'va_coil',
  'va_p11',
  'va_p09',
  'va_p12',
  'va_chieftain',
];

export const venomAltar: DungeonDef = {
  id: DUNGEON.venomAltar,
  name: 'The Venom Altar',
  // v2 layout: ~18 s less walking than v1 (docs/design/dungeon-layout-study.md §7) → 17:40.
  // +patrols: patrols, wanderers and splittable packs add ~10 mobs and ~3½ min at +10 (harness,
  // competent bots, ilvl 240: 15:16 → 18:46), so the timer keeps the same 0.86 slack at 21:50
  // (docs/design/mob-movement.md §9).
  timerMs: (21 * 60 + 50) * 1000,
  forcesRequired: 275,
  entrance: ENTRANCE,
  entranceFacing: Math.PI / 2,
  rooms: ROOMS,
  corridors: CORRIDORS,
  obstacles: OBSTACLES,
  doors: DOORS,
  props: PROP_HINTS,
  packs: [...PACKS.map(toPackDef), ...PATROLS.map(patrolDef), ...WANDERERS.map(wandererDef)],
  route: VENOM_ALTAR_ROUTE,
  requiredEncounters: [ENCOUNTER.ravager, ENCOUNTER.coil, ENCOUNTER.chieftain],
  bounds: BOUNDS,
};

/** +world-resources: breakables, hidden caches and reagent nodes, placed from the layout above. */
venomAltar.worldObjects = venomAltarWorldObjects(venomAltar);
